import {
  DEFAULT_PAGE,
  createListSearchSchema,
  filterConditionSchema,
} from '@smartsash/data-screen';
import { z } from 'zod';

/**
 * Rows per list page.
 *
 * The design carries no rows-per-page control, so this is the one number every list screen asks the
 * server for and hands the table — which is what keeps the client page and the server page the same
 * page. It replaces the constant that lived in `useListPageParam` until HS-869 retired that hook.
 */
export const LIST_PAGE_SIZE = 10;

/**
 * The extra shape every admin data-screen route validates on top of the package's list search
 * (HS-869).
 *
 * Two things it must not lose. `validateSearch` parses with this schema and `z.object` strips
 * unknown keys, so the filter builder's `?filters=` has to be declared here or it would silently
 * vanish on the first navigation — a state the user applied, gone without a trace. And admin's
 * page size is 10, not the package default, so it is pinned here rather than at each call site.
 *
 * Every field `catch`es rather than throws: search params are user-editable text, and a hand-typed
 * or stale value should degrade to the default instead of failing the route.
 */
export const adminListShape = {
  pageSize: z.coerce.number().int().positive().catch(LIST_PAGE_SIZE),
  filters: z.array(filterConditionSchema).optional().catch(undefined),
} as const;

/**
 * The list search every admin data-screen route validates.
 *
 * Spread `adminListShape` when a route adds parameters of its own:
 * `createListSearchSchema({ ...adminListShape, groupId: z.string().optional().catch(undefined) })`.
 */
export function createAdminListSearchSchema() {
  return createListSearchSchema(adminListShape);
}

/**
 * The search a link into a list route must carry.
 *
 * A validated route makes its search params part of the target's type, so navigating to a list from
 * elsewhere (a detail page's Back, a saved edit) has to name them. Naming the defaults in one place
 * keeps `page`/`pageSize` out of the call sites.
 */
export const ADMIN_LIST_SEARCH_DEFAULTS = {
  page: DEFAULT_PAGE,
  pageSize: LIST_PAGE_SIZE,
} as const;
